// WebAuthn helper functions for biometric signatures

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padLen = (4 - (base64.length % 4)) % 4
  const padded = base64 + '='.repeat(padLen)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export function isWebAuthnSupported() {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext === true &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function'
  )
}

export async function isBiometricAvailable() {
  if (!isWebAuthnSupported()) return false
  try {
    if (
      typeof PublicKeyCredential === 'function' &&
      typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
    ) {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    }
    return false
  } catch {
    return false
  }
}

export async function registerSigner(rpId, signerName) {
  if (!isWebAuthnSupported()) throw new Error('WebAuthn non supporté sur cet appareil')

  // Get challenge from server
  const res = await fetch(`/api/webauthn/register/begin?signer_name=${encodeURIComponent(signerName)}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('budget_token')}` }
  })
  const options = await res.json()
  if (!res.ok) throw new Error(options.error || 'Erreur')

  // Create credential
  const publicKey = {
    challenge: base64urlToBuffer(options.challenge),
    rp: { name: options.rp.name, id: options.rp.id },
    user: {
      id: base64urlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    authenticatorSelection: options.authenticatorSelection,
    timeout: options.timeout,
    attestation: options.attestation,
  }

  let credential
  try {
    credential = await navigator.credentials.create({ publicKey })
  } catch (e) {
    if (e && e.name === 'NotAllowedError') {
      throw new Error('Enregistrement annulé ou non autorisé')
    }
    throw e
  }

  // Send to server
  const response = await fetch('/api/webauthn/register/complete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('budget_token')}`,
    },
    body: JSON.stringify({
      signer_name: signerName,
      credential_id: bufferToBase64url(credential.rawId),
      public_key: bufferToBase64url(credential.response.getPublicKey() || credential.rawId),
    }),
  })

  const result = await response.json()
  if (!response.ok) throw new Error(result.error || 'Erreur enregistrement')
  return result
}

export async function verifyBiometric(rpId) {
  if (!isWebAuthnSupported()) throw new Error('WebAuthn non supporté')

  // Generate a local challenge for verification
  const challenge = crypto.getRandomValues(new Uint8Array(32))

  const publicKey = {
    challenge: challenge.buffer,
    rpId: rpId,
    userVerification: 'required',
    timeout: 60000,
  }

  // Use get() to verify the user is present with biometrics
  if (!navigator.credentials || typeof navigator.credentials.get !== 'function') {
    throw new Error('API credentials indisponible')
  }
  const assertion = await navigator.credentials.get({ publicKey })
  return {
    verified: true,
    credential_id: bufferToBase64url(assertion.rawId),
  }
}
